/**
 * What comes off whom, and how it flies.
 *
 * Two things decide a blow's effect. The attacker's weapon decides the *manner*
 * of the damage — a spear punches armour off at an angle, a lance sends it two
 * squares downrange, magic never touches the victim at all — and the victim's
 * build decides *what there is to lose*. Crossing the two gives the shed order
 * for any matchup, so the choreography can stay declarative.
 *
 * Nothing here touches three.js state; it only reads `userData.parts` to skip
 * armour a given victim does not carry.
 */

/** The manner of damage, keyed by attacker type. */
export const DAMAGE_BY_ATTACKER = {
  // The chibi pawn swings a short bronze sword now, not a spear.
  p: 'slash',
  n: 'charge',
  // The bishop used to be a caster and never touched its victim. It carries a
  // book now, and shuts it before it swings, so it lands.
  b: 'swat',
  // The rook is a castle now, and fights with the gun behind its port.
  r: 'bombard',
  q: 'arcane',
  k: 'cleave'
};

/**
 * How wreckage leaves the body, per manner of damage.
 *
 * `beat` is the gap between blows: a spear jabs quickly, a golem winds up.
 */
const MANNER = {
  // Precise, one thing at a time. The most blows of any attacker. Kept for
  // damageList's fallback; no attacker uses it directly any more.
  pierce: {
    fly: { power: 2.0, lift: 1.3, spread: 0.6, spin: 9 },
    beat: 1.55, maxBeats: 5, collapse: 'kneel', spark: { color: 0xff6a4a, count: 30 },
    // A thrust knocks armour off cleanly; only stone chips.
    breakMode: 'chip'
  },
  // Quick, close, one-handed cuts — the pawn's short sword. Same tempo and
  // breakMode as pierce (armour comes off clean; only stone chips), since the
  // point is a fast, light strike rather than a heavy one.
  slash: {
    fly: { power: 1.9, lift: 1.2, spread: 0.65, spin: 10 },
    beat: 1.5, maxBeats: 5, collapse: 'kneel', spark: { color: 0xd9c48a, count: 28 },
    breakMode: 'chip'
  },
  // One enormous impulse at the pass, thrown downrange rather than upward.
  // Powerful enough to clear a square or two — much beyond that and most of
  // the wreckage leaves the board, which loses the very thing it is for.
  charge: {
    fly: { power: 2.3, lift: 0.8, spread: 0.45, spin: 12 },
    beat: 2.1, maxBeats: 4, collapse: 'topple', spark: { color: 0xffd27a, count: 46, speed: 4 },
    breakMode: 'split',
    // The lance is long (0.70-0.80, nearly a full square) and couched well
    // forward of the horse for the pass, so the charge still reads as
    // connecting from a bit further back than a hand weapon needs.
    standoff: 0.65
  },
  // Never touches the victim. Straps fail and armour drifts off rather than
  // being struck away, so the throw is gentle and mostly upward.
  arcane: {
    fly: { power: 0.5, lift: 1.15, spread: 0.4, spin: 4, gravityScale: 0.55 },
    beat: 1.8, maxBeats: 4, collapse: 'vanish',
    spark: { color: 0x9a6cff, count: 54, speed: 2.4, life: 1.0, size: 0.08 },
    // Nothing is struck, so nothing breaks — it comes apart intact.
    breakMode: null,
    // A spell needs no proximity at all — the victim's own armour is what
    // animates, not anything travelling from the caster — so it can stay as
    // close to its own square as the castle does.
    standoff: 0.8
  },
  // Iron shot at close range. Four, because each one is a whole reload — so
  // the beats are few, far apart, and each takes something big.
  // Nothing is driven downward: a ball punches straight through and carries
  // the wreckage downrange with it.
  bombard: {
    fly: { power: 2.6, lift: 0.9, spread: 0.5, spin: 8 },
    // Toppled, not shattered: `shatter` crumbles a victim straight down, which
    // reads as masonry giving way. A ball punches through and throws whatever
    // it hits over backwards, the way the knight's lance does.
    beat: 2.4, maxBeats: 4, collapse: 'topple',
    spark: { color: 0xffb457, count: 58, speed: 4.2, life: 0.8 },
    breakMode: 'split',
    // A castle only needs line of sight, not weapon reach, so it stops
    // further out than the default melee distance. It stays there until the
    // victim actually falls, at which point the existing post-victory advance
    // carries it onto the cleared square.
    standoff: 0.82
  },
  // Slow, huge and downward. Debris is driven into the board, not thrown.
  crush: {
    fly: { power: 0.65, lift: 0.15, spread: 0.7, spin: 3, restitution: 0.12 },
    beat: 1.8, maxBeats: 5, collapse: 'shatter',
    spark: { color: 0xbdbdb0, count: 64, speed: 3.4, life: 0.9 },
    breakMode: 'dent'
  },
  // A book, shut and swung flat. Not much mass behind it, so armour is knocked
  // askew rather than sent downrange, and it dents rather than cleaves.
  swat: {
    fly: { power: 1.4, lift: 0.7, spread: 0.9, spin: 7 },
    beat: 1.5, maxBeats: 5, collapse: 'kneel',
    spark: { color: 0xf2e4c0, count: 22, speed: 2.4, life: 0.5 },
    breakMode: 'dent'
  },
  // Wide arcs, one large thing per swing, thrown along the line of the swing.
  cleave: {
    fly: { power: 2.4, lift: 1.05, spread: 1.0, spin: 8 },
    beat: 1.75, maxBeats: 5, collapse: 'kneel', spark: { color: 0xfff0a0, count: 50, speed: 3.8 },
    // A sword's length is the reach, so the king fights from the square
    // beside his victim rather than closing onto it. Shorter than the rook's
    // 0.82: artillery needs only line of sight, a blade needs to land.
    standoff: 0.70,
    breakMode: 'split'
  }
};

/**
 * What a part turns into when it is broken, by the manner that broke it.
 *
 * `shed` comes away, `stays` is left standing on the body. A missing entry
 * means the part simply comes off intact, which is the right answer for a
 * clean thrust or for magic that never touches it.
 *
 * Canonical parts (chunks, the bent circlet, a sheared battlement) carry a
 * `size`, because they are authored unit-sized and scaled per piece.
 */
/**
 * A weapon snapped in two. The shaft part is authored unit-sized with one
 * splintered end, so both halves come from it — the upper one flipped, so the
 * breaks face each other.
 */
const snapped = (length, radius = 0.017) => {
  const half = length / 2;
  const size = [radius * 2, half, radius * 2];
  return {
    shed: [
      { part: 'shaftBreak', size, offset: [0, -half / 2, 0] },
      { part: 'shaftBreak', size, offset: [0, half / 2, 0], rotate: Math.PI }
    ]
  };
};

/**
 * The knight's lance, as the sculpt actually carries it.
 *
 * Measured from `knight.glb` rather than guessed — `import_knight.py` prints
 * these under "measurements" — because a snapped shaft that is not the length
 * of the lance it came off is the most visible way this pipeline goes wrong.
 * The overall part is 0.0768 x 0.0861 x 0.6983; the radius is taken from the
 * shaft rather than that bounding box, which includes the grip and vamplate.
 */
const LANCE = { length: 0.70, radius: 0.023 };

/**
 * The knight's own lance, shattered against stone.
 *
 * The only slot that is ever shed from an *attacker*, which survives the fight
 * — so it carries a break rather than coming off whole. The break hides the
 * intact lance and sheds two splintered halves in its place, and resetPose puts
 * the lance back afterwards.
 */
export const ATTACKER_LANCE = {
  name: 'weapon',
  kind: 'long',
  material: 'wood',
  fly: { power: 1.5, lift: 1.1, spread: 0.8, spin: 9 },
  breakInto: snapped(LANCE.length, LANCE.radius)
};

/** An arm taken off at the shoulder, leaving a stump on the body. */
const SEVER_ARM = { shed: ['severedArm'], stays: 'armStump' };

const SHIELD_BREAK = { shed: ['shieldHalfL', 'shieldHalfR'] };
const HELM_BREAK = { shed: ['helmSplitFront', 'helmSplitBack'] };

const GATE_SPALL = {
  shed: [{ part: 'chunkA', size: [0.06, 0.07, 0.03] },
         { part: 'chunkB', size: [0.055, 0.06, 0.028] },
         { part: 'chunkC', size: [0.05, 0.065, 0.03] }]
};
const MERLON_SPALL = {
  shed: [{ part: 'chunkA', size: [0.05, 0.05, 0.05] },
         { part: 'chunkB', size: [0.045, 0.055, 0.05] }],
  stays: { part: 'merlonBroken', size: [0.075, 0.1, 0.075] }
};

/**
 * The helm halves, brought down to the sculpted knight's head.
 *
 * `helmSplitFront`/`helmSplitBack` are cut from the *kettle* helm — a 0.23-wide
 * dome — while the sculpt wears a narrow bascinet measuring 0.077 across and
 * 0.131 tall. `partSized` applies a scale factor rather than an absolute size,
 * so the shared halves can be brought to the right bulk without modelling a
 * second set. They are not the same shape, but at the size and speed they fly
 * they read as a cloven helm.
 */
const KNIGHT_HELM_SCALE = [0.34, 1.05, 0.47];
const KNIGHT_HELM_BREAK = {
  shed: [{ part: 'helmSplitFront', size: KNIGHT_HELM_SCALE },
         { part: 'helmSplitBack', size: KNIGHT_HELM_SCALE }]
};
/** The same helm, caved in rather than cloven — and scaled the same way. */
const KNIGHT_HELM_DENT = {
  shed: [{ part: 'helmDented', size: KNIGHT_HELM_SCALE }]
};

/**
 * What each victim has to lose, in the order it comes off.
 *
 * The order is the design, not the length: `damageList` takes the *first* slots
 * that resolve and stops at the manner's `maxBeats`, which is four for a
 * charge or a spell and five for a thrust, a crush or a cleave. Anything past
 * that is a reserve — it is only ever seen when something ahead of it is missing.
 */
const INVENTORY = {
  // The chibi pawn, in the order the reference diagram comes apart: the plume
  // is shorn off first — a cheap, characterful opener that costs the figure
  // nothing structural — then the helm, a pauldron, the shield, and the
  // sword. `maxBeats` is five, so everything past the sword is a reserve
  // that only shows when something ahead of it is missing.
  p: [
    { name: 'plume', impact: 'head', kind: 'flat', material: 'cloth',
      fly: { gravityScale: 0.5, spin: 5 } },
    // The helm, shield and sword are imported sculpts now (props.glb), and
    // they come away whole. Their old break geometry was cut to the shapes
    // they replaced — two halves of a 0.125 sphere for the helm, a blade
    // snapped at 0.26 — and `INVENTORY` is keyed by piece type alone, so
    // there is no way to express one split per helmet when a pawn can be
    // wearing any of three. Whole is also the truer read for a solid helm,
    // and it is what the cuirass below already does.
    { name: 'helm', impact: 'head', kind: 'compact', material: 'steel' },
    { name: 'pauldronR', impact: 'chest', kind: 'flat', material: 'steel',
      breaks: { split: { shed: ['pauldronCracked'] }, dent: { shed: ['pauldronCracked'] } } },
    { name: 'shield', impact: 'shield', kind: 'flat', material: 'steel' },
    { name: 'weapon', impact: 'weapon', kind: 'long', material: 'steel' },
    // The cuirass and the mail skirt have no broken state and come off whole,
    // which is right here: the reference *is* an assembly diagram.
    { name: 'cuirass', impact: 'chest', kind: 'flat', material: 'steel' },
    { name: 'armR', impact: 'chest', kind: 'compact', material: 'flesh',
      breaks: { chip: SEVER_ARM, split: SEVER_ARM, dent: SEVER_ARM },
      fly: { power: 0.7, lift: 0.5, spread: 0.3, spin: 4 } }
  ],
  // The knight is a sculpt, and wears rather more than the primitive figure it
  // replaced. The horse is armoured, so the fight opens on the horse — the
  // chanfron struck off its face — and works back to the rider. If knight.glb
  // is ever missing and the primitive knight is built instead, the slots it
  // cannot supply are simply skipped and the fight is shorter.
  n: [
    { name: 'chanfron', impact: 'head', kind: 'flat', material: 'steel' },
    { name: 'shield', impact: 'shield', kind: 'flat', material: 'steel',
      breaks: { split: SHIELD_BREAK, dent: SHIELD_BREAK } },
    { name: 'helm', impact: 'head', kind: 'compact', material: 'steel',
      breaks: { split: KNIGHT_HELM_BREAK, dent: KNIGHT_HELM_DENT } },
    { name: 'weapon', impact: 'weapon', kind: 'long', material: 'wood',
      breaks: { split: snapped(LANCE.length, LANCE.radius),
                dent: snapped(LANCE.length, LANCE.radius) } },
    // Reserve, in the order it would come off next.
    { name: 'crinet', impact: 'chest', kind: 'flat', material: 'steel' },
    { name: 'caparison', impact: 'chest', kind: 'flat', material: 'cloth' },
    // Comes away whole: the sculpt's arm is its own closed mesh, so it needs no
    // stand-in, and the library's severedArm is sized for the primitive rider.
    // Last, because it takes the hand and whatever is still in it.
    { name: 'armR', impact: 'chest', kind: 'compact', material: 'flesh',
      fly: { power: 0.7, lift: 0.5, spread: 0.3, spin: 4 } }
  ],
  // The bishop is a sculpt too, and carries a book rather than the staff and
  // orb the primitive figure had. Its book is the thing worth losing first —
  // it is the whole reason the piece is dangerous. As with the knight, if
  // bishop.glb is missing and the primitive is built instead, the slots it
  // cannot supply are skipped and the fight is shorter.
  b: [
    { name: 'mitre', impact: 'head', kind: 'compact', material: 'cloth' },
    // Boards and paper: it lands flat and dead, and skids rather than rolls.
    { name: 'book', impact: 'weapon', kind: 'flat', material: 'wood',
      fly: { power: 1.1, lift: 0.9, spread: 0.6, spin: 5, friction: 0.5 } },
    // Whole, not severed: the sculpt's arm is its own closed mesh, so it needs
    // no stand-in, and the library's severedArm is sized for the primitive
    // figure this one replaced. Last of the three that will be seen, because
    // it takes the hand and the book with it.
    { name: 'armR', impact: 'chest', kind: 'compact', material: 'flesh',
      fly: { power: 0.7, lift: 0.5, spread: 0.3, spin: 4 } },
    // Reserve, in the order it would come off next.
    { name: 'handL', impact: 'chest', kind: 'compact', material: 'flesh' },
    { name: 'armL', impact: 'chest', kind: 'compact', material: 'flesh',
      fly: { power: 0.7, lift: 0.5, spread: 0.3, spin: 4 } }
  ],
  // A castle is not beaten, it is dismantled — so it comes apart in the order
  // the reference's own teardown does. The siege ladder clatters off first
  // because it is only leaning there; then battlements, then the gate is
  // staved in, then the cap itself goes with whatever crenellations are left
  // standing on it.
  r: [
    { name: 'ladder', impact: 'chest', kind: 'long', material: 'wood',
      fly: { power: 1.6, lift: 0.8, spread: 0.7, spin: 6, friction: 0.5 } },
    { name: 'merlons', index: 0, impact: 'head', kind: 'heavy', material: 'stone',
      breaks: { chip: MERLON_SPALL, split: MERLON_SPALL, dent: MERLON_SPALL } },
    { name: 'merlons', index: 1, impact: 'head', kind: 'heavy', material: 'stone',
      breaks: { chip: MERLON_SPALL, split: MERLON_SPALL, dent: MERLON_SPALL } },
    { name: 'gate', impact: 'chest', kind: 'heavy', material: 'wood',
      breaks: { chip: GATE_SPALL, split: GATE_SPALL, dent: GATE_SPALL } },
    { name: 'merlons', index: 2, impact: 'head', kind: 'heavy', material: 'stone',
      breaks: { chip: MERLON_SPALL, split: MERLON_SPALL, dent: MERLON_SPALL } },
    { name: 'merlons', index: 3, impact: 'head', kind: 'heavy', material: 'stone',
      breaks: { chip: MERLON_SPALL, split: MERLON_SPALL, dent: MERLON_SPALL } },
    // The whole parapet struck off the top, carrying its remaining teeth.
    { name: 'cap', impact: 'head', kind: 'heavy', material: 'stone',
      breaks: { chip: MERLON_SPALL, split: MERLON_SPALL, dent: MERLON_SPALL },
      fly: { power: 1.2, lift: 1.0, spread: 0.5, spin: 3 } },
    // Prised out last, and it stays: a bright ember left lying on the board.
    { name: 'core', impact: 'chest', kind: 'compact', material: 'glass',
      fly: { power: 0.4, lift: 1.9, spread: 0.2 }, permanent: true }
  ],
  q: [
    { name: 'crown', impact: 'head', kind: 'compact', material: 'steel',
      breaks: { split: { shed: [{ part: 'crownBent', size: [0.176, 0.045, 0.176] }] },
                dent: { shed: [{ part: 'crownBent', size: [0.176, 0.045, 0.176] }] } } },
    { name: 'orb', impact: 'weapon', kind: 'compact', material: 'glass',
      fly: { restitution: 0.5, spin: 5 } },
    { name: 'weapon', impact: 'weapon', kind: 'long', material: 'wood',
      breaks: { split: snapped(0.58), dent: snapped(0.58) } },
    // Cloth is light: it drifts and settles slowly rather than clattering.
    { name: 'cape', impact: 'chest', kind: 'flat', material: 'cloth',
      breaks: { split: { shed: ['capeTorn'] }, dent: { shed: ['capeTorn'] } },
      fly: { gravityScale: 0.45, friction: 0.35, spin: 3 } },
    { name: 'sash', impact: 'chest', kind: 'flat', material: 'cloth',
      fly: { gravityScale: 0.5, spin: 3 } }
  ],
  k: [
    { name: 'pauldronL', impact: 'chest', kind: 'flat', material: 'steel',
      breaks: { split: { shed: ['pauldronCracked'] }, dent: { shed: ['pauldronCracked'] } } },
    { name: 'pauldronR', impact: 'chest', kind: 'flat', material: 'steel',
      breaks: { split: { shed: ['pauldronCracked'] }, dent: { shed: ['pauldronCracked'] } } },
    // Shed whole, unlike the queen's. `crownBent` is a flat bent circlet
    // 0.045 tall; the sculpted king wears a domed cap measured at
    // 0.163 x 0.221 x 0.176, and stretching a band to that height reads as a
    // stretched band, not a crown. Knocked off intact it reads as itself.
    { name: 'crown', impact: 'head', kind: 'compact', material: 'steel' },
    { name: 'weapon', impact: 'weapon', kind: 'long', material: 'wood',
      breaks: {
        split: { shed: [{ part: 'bladeSnapped', size: [1, 0.46, 1], offset: [0, 0.27, 0] }] },
        dent: { shed: [{ part: 'bladeSnapped', size: [1, 0.46, 1], offset: [0, 0.27, 0] }] }
      } },
    { name: 'cape', impact: 'chest', kind: 'flat', material: 'cloth',
      breaks: { split: { shed: ['capeTorn'] }, dent: { shed: ['capeTorn'] } },
      fly: { gravityScale: 0.45, friction: 0.35, spin: 3 } },
    // The sculpted king's ermine, and the collar chain under it. Slots that
    // do not resolve are skipped, so one list serves both builds: the
    // primitive spends its five beats on the pauldrons, crown, sword and
    // cape, the sculpt (which has no pauldrons and no cape) on the crown,
    // sword, cloak, chain and whatever else resolves next.
    { name: 'cloak', impact: 'chest', kind: 'flat', material: 'cloth',
      breaks: { split: { shed: ['capeTorn'] }, dent: { shed: ['capeTorn'] } },
      fly: { gravityScale: 0.45, friction: 0.35, spin: 3 } },
    { name: 'chain', impact: 'chest', kind: 'compact', material: 'steel' },
    { name: 'tabard', impact: 'chest', kind: 'flat', material: 'cloth',
      fly: { gravityScale: 0.5, spin: 3 } }
  ]
};

/** Resolves a slot to the live object on the piece, or null if it has none. */
export function resolveSlot(piece, slot) {
  const held = piece.userData.parts ?? {};
  const node = slot.index === undefined ? held[slot.name] : held[slot.name]?.[slot.index];
  return node ?? null;
}

/**
 * The ordered shed list for one matchup.
 *
 * Slots the victim cannot supply are dropped, so a rook — which has no helm to
 * knock off — simply loses masonry instead, and the fight is as long as it has
 * things to lose.
 */
export function damageList(attacker, victim) {
  const mannerName = DAMAGE_BY_ATTACKER[attacker.userData.type] ?? 'pierce';
  const manner = MANNER[mannerName];
  const inventory = INVENTORY[victim.userData.type] ?? [];

  const slots = [];
  for (const entry of inventory) {
    if (!resolveSlot(victim, entry)) continue;
    slots.push({
      ...entry,
      fly: { ...manner.fly, ...(entry.fly ?? {}) },
      kind: entry.kind,
      material: entry.material ?? 'steel',
      permanent: !!entry.permanent,
      // Resolved here so the animator does not have to know the manner.
      breakInto: manner.breakMode ? (entry.breaks?.[manner.breakMode] ?? null) : null
    });
    if (slots.length >= manner.maxBeats) break;
  }

  return { manner: mannerName, slots, ...manner };
}

/**
 * A spear against a stone golem should look futile. Softens the sparks and
 * flattens the victim's reaction where the matchup calls for it.
 */
export function isMismatched(attacker, victim) {
  return victim.userData.type === 'r'
    && (attacker.userData.type === 'p' || attacker.userData.type === 'b');
}
